import { MNSproCloudApi } from '..';
import {
  ErrorResponse,
  InvitationCreateRequest,
  InvitationFeature,
  InvitationResponse,
  InvitationUpdateRequest,
} from '../api/v2';

export class InvitationsService {
  private static createInvitationsApi(authToken: string) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.InvitationApiFactory(config);

    return api;
  }

  /**
   * Get all Invitations, the current user has access to.
   *
   * @export
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @return {*}  {Promise<InvitationResponse[]>}
   */
  static async getAll(authToken: string): Promise<InvitationResponse[]> {
    const api = InvitationsService.createInvitationsApi(authToken);

    const r = await api.getAll();
    return r.data;
  }

  /**
   * Get standard FeatureSet for invitations, the current user has access to.
   *
   * @export
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @return {*}  {Promise<Feature[]>}
   */
  static async getFeatureSet(
    authToken: string,
    invitationId?: string,
  ): Promise<InvitationFeature[]> {
    const api = InvitationsService.createInvitationsApi(authToken);

    const featureSet = await api.getFeatures(invitationId);
    return featureSet.data;
  }

  /**
   * create invitation.
   *
   * @export
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @return {*}  {Promise<InvitationResponse[]>}
   */
  static async createInvitation(
    authToken: string,
    createinvitation: InvitationCreateRequest,
  ): Promise<InvitationResponse> {
    const api = InvitationsService.createInvitationsApi(authToken);

    const r = await api.create(createinvitation);

    if (r.data) {
      await api.sendMail(r.data.id ?? '');
    }
    return r.data;
  }

  /**
   * send invitation.
   *
   * @export
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @return {*}  {Promise<InvitationResponse>}
   */
  static async sendInvitation(authToken: string, invitationId: string) {
    const api = InvitationsService.createInvitationsApi(authToken);

    const r = await api.sendMail(invitationId);
  }

  /**
   * update invitation.
   *
   * @export
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @return {*}  {Promise<InvitationResponse>}
   */
  static async editInvitation(
    authToken: string,
    updateInvitation: InvitationUpdateRequest,
  ): Promise<InvitationResponse> {
    const api = InvitationsService.createInvitationsApi(authToken);

    const r = await api.update(updateInvitation);

    if (r.data) {
      await api.sendMail(r.data.id ?? '');
    }
    return r.data;
  }

  /**
   * delete invitation.
   *
   * @export
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @return {*}  {Promise<InvitationResponse>}
   */
  static async deleteInvitation(
    authToken: string,
    invitationId: string,
  ): Promise<ErrorResponse> {
    const api = InvitationsService.createInvitationsApi(authToken);

    const r = await api.deleteInvitation(invitationId);
    return r.data;
  }

  /**
   * add attachment invitation.
   *
   * @export
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @return {*}  {Promise<InvitationResponse>}
   */
  static async addInvitationAttachment(
    authToken: string,
    invitationId: string,
    formfile: any,
  ): Promise<void> {
    const api = InvitationsService.createInvitationsApi(authToken);

    const r = await api.addAttachment(invitationId, formfile);
  }

  /**
   * delete attachment invitation.
   *
   * @export
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @return {*}  {Promise<InvitationResponse>}
   */
  static async deleteInvitationAttachment(
    authToken: string,
    invitationId: string,
    fileId: string,
  ): Promise<void> {
    const api = InvitationsService.createInvitationsApi(authToken);

    const r = await api.deleteAttachment(invitationId);
  }

  /**
   * get attachments of invitation.
   *
   * @export
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @return {*}  {Promise<InvitationResponse>}
   */
  static async getAttachment(
    authToken: string,
    invitationId: string,
  ): Promise<string> {
    const api = InvitationsService.createInvitationsApi(authToken);

    const r = await api.getAttachment(invitationId);
    return r.data;
  }
}
